/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { decodeError } from '@genially/ui/build/utils/decodeError';

export class ProcessingQueue {
  private readonly processingQueue = new Map<string, Promise<void>>();

  /**
   * Makes sure that a command is not executed until the last is finished.
   */
  async queue(
    queueId: string,
    fn: () => Promise<void>,
    onError?: (err: unknown) => void,
  ): Promise<void> {
    let promise = this.processingQueue.get(queueId);
    if (!promise) {
      promise = Promise.resolve();
      this.processingQueue.set(queueId, promise);
    }

    const newPromise = promise
      .then(async () => {
        try {
          await fn();
        } catch (e) {
          const err = e as Error;

          if (onError) {
            onError(err);
          } else {
            // eslint-disable-next-line no-console
            console.warn(
              `[ProcessingQueue - ${queueId}: Failed with error`,
              decodeError(err),
            );
          }
        }
      })
      .finally(() => {
        if (this.processingQueue.get(queueId) === newPromise) {
          // cleanup if this is the last task in the queue
          this.processingQueue.delete(queueId);
        }
      });

    this.processingQueue.set(queueId, newPromise);

    return newPromise;
  }

  /**
   * Waits for a queue to be empty.
   */
  async waitForQueue(queueId: string): Promise<void> {
    let currentPromise = this.processingQueue.get(queueId);
    while (currentPromise) {
      // eslint-disable-next-line no-await-in-loop
      await currentPromise;
      currentPromise = this.processingQueue.get(queueId);
    }
  }

  /**
   * Waits for all queues to be empty.
   */
  async waitForAllQueues(): Promise<void> {
    while (this.processingQueue.size > 0) {
      const allPromises = Array.from(this.processingQueue.values());
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(allPromises);
    }
  }
}
